import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../render/layout';
import { allPuppies } from '../game/Puppy';
import { PUPPY_ANCHORS, PUPPY_MAX_DIM } from '../render/puppyAnchors.generated';
import type { PuppyId } from '../game/types';

// Per-puppy translation to keep the visible face centred in the yellow
// box (the nose anchor isn't always at the face's geometric centre, so we
// nudge each puppy to compensate). Tuned visually with the AnimDebugScene.
const PUPPY_FACE_NUDGE: Partial<Record<PuppyId, { dx: number; dy: number }>> = {
  doug: { dx: 9, dy: 6 },
  zoe: { dx: 0, dy: 30 },
};

// Per-puppy animation counts (Bella has 4, everyone else has 5).
const NUM_ANIMS: readonly number[] = [5, 5, 5, 5, 5, 5, 4, 5, 5, 5];

// Persists in-progress alignment offsets so they survive scene reload / page
// refresh while tuning. The final values get dumped to the console on close
// and the developer pastes them into source.
const ALIGNMENT_STORAGE_KEY = 'puppy-parks-alignment-offsets-v1';

interface Offset { dx: number; dy: number }
interface AlignmentData {
  stills: Partial<Record<PuppyId, Offset>>;
  anims: Partial<Record<PuppyId, Offset[]>>;
}

// Alignment-tuning viewer. Shows the still frame (pNa1/frame1) overlaid with
// the first frame of the currently selected animation (pNaK/frame1) so the
// developer can visually nudge each into place. The arrow keys nudge the
// anim frame; Cmd+arrow nudges the still; Shift+arrow navigates between
// puppies and anims. ESC dumps the resulting offsets to console.

interface AnimDebugInit {
  onClose?: () => void;
  puppyIdx?: number;
  animIdx?: number;
}

export class AnimDebugScene extends Phaser.Scene {
  private puppyIdx = 0;
  private animIdx = 0;
  private stillSprite!: Phaser.GameObjects.Sprite;
  private animSprite!: Phaser.GameObjects.Sprite;
  private label!: Phaser.GameObjects.Text;
  private offsetLabel!: Phaser.GameObjects.Text;
  private hint!: Phaser.GameObjects.Text;
  private onClose: (() => void) | null = null;
  private centerX = LOGICAL_WIDTH / 2;
  private centerY = LOGICAL_HEIGHT / 2 - 40;
  // The crazysquareframe asset is 73×73 native; the red FILL occupies
  // (0..68, 0..68) and the bottom-right 5px is a drop shadow.
  private frameDisplaySize = 560;
  private redFillSize = this.frameDisplaySize * (68 / 73);
  private redFillCentreOffset = -(this.frameDisplaySize * (5 / 73)) / 2;
  private get boxCx(): number { return this.centerX + this.redFillCentreOffset; }
  private get boxCy(): number { return this.centerY + this.redFillCentreOffset; }
  private puppyTargetSize = 450;

  private alignment: AlignmentData = { stills: {}, anims: {} };

  constructor() {
    super({ key: 'AnimDebug' });
  }

  init(data: AnimDebugInit): void {
    this.onClose = data?.onClose ?? null;
    this.puppyIdx = data?.puppyIdx ?? 0;
    this.animIdx = data?.animIdx ?? 0;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#5fa84a');

    this.add
      .image(this.centerX, this.centerY, 'crazysquareframe')
      .setOrigin(0.5)
      .setDisplaySize(this.frameDisplaySize, this.frameDisplaySize);

    const yellowPadding = 20;
    const yellowSize = this.redFillSize - yellowPadding * 2;
    this.add
      .rectangle(this.boxCx, this.boxCy, yellowSize, yellowSize, 0x000000, 0)
      .setStrokeStyle(2, 0xffff00)
      .setDepth(50);

    this.loadAlignment();

    // Still and anim-frame-1 sprites overlaid. Different alphas so the
    // developer can see both at once and judge the misalignment.
    this.stillSprite = this.add
      .sprite(this.boxCx, this.boxCy, 'puppieslg', 'puppy1@2x.png')
      .setOrigin(0.5)
      .setAlpha(0.5)
      .setDepth(60);
    this.animSprite = this.add
      .sprite(this.boxCx, this.boxCy, 'puppieslg', 'puppy1@2x.png')
      .setOrigin(0.5)
      .setAlpha(0.7)
      .setDepth(70);

    const labelY = this.centerY + this.frameDisplaySize / 2 + 60;
    this.label = this.add
      .text(this.centerX, labelY, '', {
        fontFamily: 'SnackerComic',
        fontSize: '48px',
        color: '#ffeb00',
        stroke: '#cc0000',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    this.offsetLabel = this.add
      .text(this.centerX, labelY + 60, '', {
        fontFamily: 'system-ui',
        fontSize: '24px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    this.hint = this.add
      .text(
        this.centerX,
        LOGICAL_HEIGHT - 60,
        '← → ↑ ↓ nudge anim   ⌥+arrow nudge still   ⇧+arrow navigate   SPACE play   ESC done',
        {
          fontFamily: 'system-ui',
          fontSize: '18px',
          color: '#ffffff',
        },
      )
      .setOrigin(0.5);
    void this.hint;

    this.refresh();

    const kb = this.input.keyboard;
    kb?.on('keydown-LEFT', (e: KeyboardEvent) => this.onArrow('left', e));
    kb?.on('keydown-RIGHT', (e: KeyboardEvent) => this.onArrow('right', e));
    kb?.on('keydown-UP', (e: KeyboardEvent) => this.onArrow('up', e));
    kb?.on('keydown-DOWN', (e: KeyboardEvent) => this.onArrow('down', e));
    kb?.on('keydown-SPACE', () => this.playCurrentAnim());
    kb?.on('keydown-ESC', () => this.close());
  }

  // Plays the currently selected animation once on the anim sprite using
  // the developer's current anim-offset, so they can verify that the offset
  // they tuned at frame 1 stays right through the whole motion. Still sprite
  // remains in place as the reference.
  private playCurrentAnim(): void {
    const puppy = allPuppies()[this.puppyIdx]!;
    const puppyNum = puppy.puppyNum;
    const animNum = this.animIdx + 1;
    const animKey = `puppy${puppyNum}.anim${animNum}`;
    const atlasKey = `p${puppyNum}a${animNum}`;
    if (!this.anims.exists(animKey)) return;

    const offset = this.getAnim(puppy.id, this.animIdx);
    this.animSprite.off('animationupdate');
    this.animSprite.on(
      'animationupdate',
      (_anim: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
        this.applyFrameAnchor(this.animSprite, atlasKey, frame.textureFrame as string, offset);
      },
    );
    this.animSprite.play({ key: animKey, repeat: 0 });
    const firstFrame = this.anims.get(animKey)?.frames[0]?.textureFrame as string | undefined;
    if (firstFrame) this.applyFrameAnchor(this.animSprite, atlasKey, firstFrame, offset);

    this.animSprite.once('animationcomplete', () => {
      this.animSprite.off('animationupdate');
      this.animSprite.setTexture(atlasKey, 'frame1');
      this.applyFrameAnchor(this.animSprite, atlasKey, 'frame1', offset);
    });
  }

  private onArrow(dir: 'left' | 'right' | 'up' | 'down', e: KeyboardEvent): void {
    if (e.shiftKey) {
      if (dir === 'left') this.cycleAnim(-1);
      else if (dir === 'right') this.cycleAnim(1);
      else if (dir === 'up') this.cyclePuppy(-1);
      else if (dir === 'down') this.cyclePuppy(1);
      return;
    }
    const dx = dir === 'left' ? -1 : dir === 'right' ? 1 : 0;
    const dy = dir === 'up' ? -1 : dir === 'down' ? 1 : 0;
    if (e.altKey) this.nudgeStill(dx, dy);
    else this.nudgeAnim(dx, dy);
  }

  private currentPuppyId(): PuppyId {
    return allPuppies()[this.puppyIdx]!.id;
  }

  private getStill(puppyId: PuppyId): Offset {
    return this.alignment.stills[puppyId] ?? { dx: 0, dy: 0 };
  }

  private getAnim(puppyId: PuppyId, animIdx: number): Offset {
    return this.alignment.anims[puppyId]?.[animIdx] ?? { dx: 0, dy: 0 };
  }

  private nudgeStill(dx: number, dy: number): void {
    const id = this.currentPuppyId();
    const cur = this.getStill(id);
    this.alignment.stills[id] = { dx: cur.dx + dx, dy: cur.dy + dy };
    this.saveAlignment();
    this.refresh();
  }

  private nudgeAnim(dx: number, dy: number): void {
    const id = this.currentPuppyId();
    const cur = this.getAnim(id, this.animIdx);
    const arr = (this.alignment.anims[id] ??= []);
    arr[this.animIdx] = { dx: cur.dx + dx, dy: cur.dy + dy };
    this.saveAlignment();
    this.refresh();
  }

  private maxAnim(): number {
    return NUM_ANIMS[this.puppyIdx] ?? 5;
  }

  private cycleAnim(dir: number): void {
    const max = this.maxAnim();
    this.animIdx = (this.animIdx + max + dir) % max;
    this.refresh();
  }

  private cyclePuppy(dir: number): void {
    this.puppyIdx = (this.puppyIdx + 10 + dir) % 10;
    this.animIdx = 0;
    this.refresh();
  }

  // Position sprite using the per-frame nose anchor + per-puppy face nudge
  // + the offset the developer is tuning.
  private applyFrameAnchor(
    sprite: Phaser.GameObjects.Sprite,
    atlasKey: string,
    frameName: string,
    offset: Offset,
  ): void {
    const a = PUPPY_ANCHORS[atlasKey]?.[frameName] ?? { x: 0.5, y: 0.5 };
    const puppy = allPuppies()[this.puppyIdx]!;
    const maxDim = PUPPY_MAX_DIM[String(puppy.puppyNum)] ?? 200;
    const scale = this.puppyTargetSize / maxDim;
    sprite.setScale(scale);
    sprite.setOrigin(a.x, a.y);
    const nudge = PUPPY_FACE_NUDGE[puppy.id] ?? { dx: 0, dy: 0 };
    sprite.setPosition(this.boxCx + nudge.dx + offset.dx, this.boxCy + nudge.dy + offset.dy);
  }

  private refresh(): void {
    const puppy = allPuppies()[this.puppyIdx]!;
    const puppyNum = puppy.puppyNum;
    const animNum = this.animIdx + 1;

    const stillAtlas = `p${puppyNum}a1`;
    const stillFrame = 'frame1';
    if (this.textures.exists(stillAtlas)) {
      this.stillSprite.setTexture(stillAtlas, stillFrame);
      this.applyFrameAnchor(this.stillSprite, stillAtlas, stillFrame, this.getStill(puppy.id));
    }

    const animAtlas = `p${puppyNum}a${animNum}`;
    const animFrame = 'frame1';
    if (this.textures.exists(animAtlas)) {
      this.animSprite.setTexture(animAtlas, animFrame);
      this.applyFrameAnchor(
        this.animSprite,
        animAtlas,
        animFrame,
        this.getAnim(puppy.id, this.animIdx),
      );
    }

    this.label.setText(`${puppy.displayName} — anim ${animNum}`);
    const so = this.getStill(puppy.id);
    const ao = this.getAnim(puppy.id, this.animIdx);
    this.offsetLabel.setText(`still: (${so.dx}, ${so.dy})   anim: (${ao.dx}, ${ao.dy})`);
  }

  private loadAlignment(): void {
    try {
      const raw = localStorage.getItem(ALIGNMENT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<AlignmentData>;
        this.alignment = {
          stills: parsed.stills ?? {},
          anims: parsed.anims ?? {},
        };
      }
    } catch {
      // ignore
    }
  }

  private saveAlignment(): void {
    try {
      localStorage.setItem(ALIGNMENT_STORAGE_KEY, JSON.stringify(this.alignment));
    } catch {
      // ignore
    }
  }

  private close(): void {
    // Dump in a paste-friendly shape so the developer can transfer values
    // into source (e.g. into puppyAlignmentOffsets.ts once that file exists).
    console.log('=== Puppy alignment offsets ===');
    console.log('stills:', JSON.stringify(this.alignment.stills, null, 2));
    console.log('anims:', JSON.stringify(this.alignment.anims, null, 2));
    this.scene.stop();
    this.onClose?.();
  }
}
